// Grouped alt-line cards are inserted where their FIRST line lands in the
// sorted list, but render lines[0] (the pill line). Re-rank each card by
// where its displayed line sits in `sortedOptions` so the visible order
// follows the user's sort. Cards whose displayed line isn't in the list
// (backend alternateLines copies) keep their current rank.

const EXPLICIT_SORTS = new Set(['price_asc', 'price_desc', 'odds', 'date', 'market', 'sportsbooks']);

// Sorts the user picked on a non-EV axis. Focus-book and My Books EV re-sorts
// must not override these.
export function isExplicitUserSort(sortBy) {
    return EXPLICIT_SORTS.has(sortBy);
}

// A card keeps its lines in the Additional Sorting order only when those lines came
// from the sorted list. Cards built from a backend `alternateLines` array are ordered
// main-first and never pass through that sort, so they keep the best-EV pick.
export function keepsListLineOrder(sortBy, group) {
    return isExplicitUserSort(sortBy) && !group?.fromBackendAlts;
}

export function sortCardsByDisplayedLine(cards, sortedOptions) {
    if (!Array.isArray(cards) || cards.length < 2 || !Array.isArray(sortedOptions)) return cards;
    const rankOf = new Map();
    sortedOptions.forEach((opt, i) => { if (opt && !rankOf.has(opt)) rankOf.set(opt, i); });

    const ranked = cards.map((entry, idx) => {
        const card = entry?.[1];
        const shown = Array.isArray(card?.lines) && card.lines.length > 0 ? card.lines[0] : card;
        const rank = rankOf.has(shown) ? rankOf.get(shown) : null;
        return { entry, idx, rank };
    });

    // Unranked cards inherit the rank of the card before them so they stay put.
    let prev = -1;
    for (const r of ranked) {
        if (r.rank == null) r.rank = prev + 0.5;
        else prev = r.rank;
    }

    ranked.sort((a, b) => (a.rank - b.rank) || (a.idx - b.idx));
    for (let i = 0; i < ranked.length; i++) cards[i] = ranked[i].entry;
    return cards;
}

// Single-team searches ("canes goals", "hurricanes puck line"): the backend stamps
// each item with `_teamRank` — 0 the team's own lines, 1 its players, 2 game-level
// lines (game totals), 3 the opponent's lines, 4 the opponent's players. The
// default views keep that grouping (best EV inside each group); an explicit
// Additional Sorting choice does not call this.
export function teamRankOf(option) {
    const r = option?.raw?._teamRank ?? option?._teamRank;
    return Number.isFinite(r) ? r : null;
}

/** The game's place for a single-team search (`_teamGame`, 0 = soonest), else null. */
export function teamGameOf(option) {
    const g = option?.raw?._teamGame ?? option?._teamGame;
    return Number.isFinite(g) ? g : null;
}

export function sortCardsByTeamRank(cards) {
    if (!Array.isArray(cards) || cards.length < 2) return cards;
    const lowest = (card, read) => {
        const lines = Array.isArray(card?.lines) && card.lines.length ? card.lines : [card];
        let best = null;
        for (const line of lines) {
            const r = read(line);
            if (r != null && (best == null || r < best)) best = r;
        }
        return best;
    };
    // A card whose every line is a server no-EV row sorts after the rank
    // groups within its game (functions/utils/noEvRows).
    const allNoEv = (card) => {
        const lines = Array.isArray(card?.lines) && card.lines.length ? card.lines : [card];
        return lines.length > 0 && lines.every((l) => (l?.raw?._noEv ?? l?._noEv) === true);
    };
    const ranked = cards.map((entry, idx) => ({
        entry, idx, rank: lowest(entry?.[1], teamRankOf), game: lowest(entry?.[1], teamGameOf),
        noEv: allNoEv(entry?.[1]) ? 1 : 0,
    }));
    if (!ranked.some((x) => x.rank != null)) return cards;
    // Soonest game first, then the team's side order within each game (Oct 1, 2026:
    // sorting by side alone put both games' Canes cards before either Habs card).
    ranked.sort((a, b) => ((a.game ?? 0) - (b.game ?? 0)) || (a.noEv - b.noEv) || ((a.rank ?? 2) - (b.rank ?? 2)) || (a.idx - b.idx));
    cards.splice(0, cards.length, ...ranked.map((x) => x.entry));
    return cards;
}

// Searches that name a line ("chiefs -3.5", "lebron over 25.5 points", Trev, Oct 16,
// 2026): the backend stamps `_lineMatchRank` (functions/utils/lineFirst.js; lower is
// closer: 0 the exact line on the asked side, then the other side, then the nearest
// lines). Read from the raw item or the option.
export function lineMatchRankOf(option) {
    const r = option?.raw?._lineMatchRank ?? option?._lineMatchRank;
    return Number.isFinite(r) ? r : null;
}

// Cards holding the searched line first, then the nearest lines; cards with no rank
// (no line, another market) follow in their current order. Stable, so the EV / team
// order holds inside each rank. No stamp on any card (no line in the search): the
// order is untouched. An explicit Additional Sorting choice does not call this.
export function sortCardsByLineMatch(cards) {
    if (!Array.isArray(cards) || cards.length < 2) return cards;
    const ranked = cards.map((entry, idx) => {
        const card = entry?.[1];
        const lines = Array.isArray(card?.lines) && card.lines.length ? card.lines : [card];
        let best = null;
        for (const line of lines) {
            const r = lineMatchRankOf(line);
            if (r != null && (best == null || r < best)) best = r;
        }
        return { entry, idx, rank: best };
    });
    if (!ranked.some((x) => x.rank != null)) return cards;
    ranked.sort((a, b) => {
        const ra = a.rank ?? Infinity;
        const rb = b.rank ?? Infinity;
        if (ra !== rb) return ra < rb ? -1 : 1;
        return a.idx - b.idx;
    });
    cards.splice(0, cards.length, ...ranked.map((x) => x.entry));
    return cards;
}
